import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase.js';
import { formatPhoneNumber, extractPhoneNumbers } from '../utils/validationUtils.ts';
import UserActivityLogs from '../components/UserActivityLogs.tsx';

const ProfilePage: React.FC = () => {
  const { user, updateUserProfile, updateUserPhoneNumber, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // 비밀번호 변경 관련 상태
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [sendingResetEmail, setSendingResetEmail] = useState(false);

  useEffect(() => {
    if (user) {
      console.log('[ProfilePage] User object changed, updating local state');
      console.log('[ProfilePage] User phoneNumber:', user.phoneNumber);
      
      setDisplayName(user.displayName || '');
      // 저장된 전화번호(숫자만)를 포맷팅해서 UI에 표시
      setPhoneNumber(formatPhoneNumber(user.phoneNumber || ''));
      
      // 디버깅: user 객체 확인
      console.log('ProfilePage user object:', user);
      console.log('createdAt type:', typeof user.createdAt);
      console.log('createdAt value:', user.createdAt);
      console.log('createdAt has toDate method:', user.createdAt && typeof user.createdAt.toDate === 'function');
    }
  }, [user]);

  // 전화번호 변경을 별도로 감지
  useEffect(() => {
    if (user?.phoneNumber !== undefined) {
      console.log('[ProfilePage] PhoneNumber specifically changed to:', user.phoneNumber);
      // 저장된 전화번호(숫자만)를 포맷팅해서 UI에 표시
      setPhoneNumber(formatPhoneNumber(user.phoneNumber || ''));
    }
  }, [user?.phoneNumber]);

  // 안전한 날짜 포맷팅 함수
  const formatDate = (dateField: any): string => {
    try {
      // null/undefined 체크
      if (!dateField) {
        return '알 수 없음';
      }

      // 이미 Date 객체인 경우
      if (dateField instanceof Date) {
        return dateField.toLocaleDateString('ko-KR');
      }

      // Firebase Timestamp 객체인 경우 (toDate 메서드가 있는 경우)
      if (dateField && typeof dateField.toDate === 'function') {
        return dateField.toDate().toLocaleDateString('ko-KR');
      }

      // 문자열이나 숫자인 경우 Date 생성자로 변환 시도
      if (typeof dateField === 'string' || typeof dateField === 'number') {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('ko-KR');
        }
      }

      // 객체이지만 toDate가 없는 경우, seconds 필드가 있는지 확인 (Firestore Timestamp)
      if (typeof dateField === 'object' && dateField.seconds) {
        const date = new Date(dateField.seconds * 1000);
        return date.toLocaleDateString('ko-KR');
      }

      console.warn('Unknown date format:', dateField);
      return '알 수 없음';
    } catch (error) {
      console.error('Date formatting error:', error, 'for field:', dateField);
      return '알 수 없음';
    }
  };

  const handleSave = async () => {
    if (!user) return;
    
    console.log('[ProfilePage] handleSave called');
    console.log('[ProfilePage] Current phoneNumber state:', phoneNumber);
    console.log('[ProfilePage] Current user.phoneNumber:', user.phoneNumber);
    
    setSaving(true);
    try {
      // displayName 업데이트
      await updateUserProfile({
        displayName: displayName.trim()
      });
      
      // 전화번호가 변경된 경우에만 업데이트
      const currentPhoneNumberFormatted = formatPhoneNumber(user.phoneNumber || '');
      if (phoneNumber.trim() !== currentPhoneNumberFormatted) {
        console.log('[ProfilePage] Updating phone number:', phoneNumber.trim());
        // 저장할 때는 숫자만 추출해서 저장
        const numbersOnly = extractPhoneNumbers(phoneNumber.trim());
        await updateUserPhoneNumber(numbersOnly);
        console.log('[ProfilePage] Phone number update completed');
      }
      
      setMessage('프로필이 성공적으로 업데이트되었습니다.');
      setIsEditing(false);
      
      // 메시지를 3초 후에 제거
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('프로필 업데이트 에러:', error);
      setMessage('프로필 업데이트 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(user?.displayName || '');
    // 취소 시에도 포맷팅된 형태로 복원
    setPhoneNumber(formatPhoneNumber(user?.phoneNumber || ''));
    setIsEditing(false);
  };

  // 전화번호 입력 핸들러 (자동 포맷팅 적용)
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 입력 중에도 포맷팅 적용
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
  };

  // 비밀번호 변경 처리
  const handlePasswordChange = async () => {
    if (!auth.currentUser) return;

    // 입력 검증
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage('모든 비밀번호 필드를 입력해주세요.');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage('새 비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    setPasswordChanging(true);
    try {
      // 현재 비밀번호로 재인증
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email!,
        passwordData.currentPassword
      );
      
      await reauthenticateWithCredential(auth.currentUser, credential);
      
      // 비밀번호 업데이트
      await updatePassword(auth.currentUser, passwordData.newPassword);
      
      setMessage('비밀번호가 성공적으로 변경되었습니다.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordSection(false);
      
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('비밀번호 변경 에러:', error);
      
      if (error.code === 'auth/wrong-password') {
        setMessage('현재 비밀번호가 올바르지 않습니다.');
      } else if (error.code === 'auth/weak-password') {
        setMessage('새 비밀번호가 너무 약합니다. 더 강력한 비밀번호를 사용해주세요.');
      } else {
        setMessage('비밀번호 변경 중 오류가 발생했습니다.');
      }
    } finally {
      setPasswordChanging(false);
    }
  };

  // 비밀번호 재설정 이메일 발송
  const handleSendPasswordResetEmail = async () => {
    if (!auth.currentUser?.email) return;

    setSendingResetEmail(true);
    try {
      await sendPasswordResetEmail(auth, auth.currentUser.email);
      setMessage('비밀번호 재설정 이메일이 발송되었습니다. 이메일을 확인해주세요.');
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('비밀번호 재설정 이메일 발송 에러:', error);
      setMessage('비밀번호 재설정 이메일 발송 중 오류가 발생했습니다.');
    } finally {
      setSendingResetEmail(false);
    }
  };

  // 사용자 권한 관리 페이지로 이동
  const handleUserManagement = () => {
    navigate('/user-management');
  };

  // Role display mapping
  const roleDisplayNames: Record<string, string> = {
    admin: '관리자',
    manager: '매니저',
    viewer: '뷰어',
    employee: '직원'
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">내 프로필</h1>
        <p className="text-gray-600">개인 정보를 확인하고 수정할 수 있습니다.</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-md ${
          message.includes('성공') 
            ? 'bg-green-50 border border-green-300 text-green-700' 
            : 'bg-red-50 border border-red-300 text-red-700'
        }`}>
          {message}
          <button 
            onClick={() => setMessage('')}
            className="ml-2 text-current hover:opacity-70"
          >
            ✕
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* 기본 정보 섹션 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">기본 정보</h2>
            
            {/* 프로필 헤더 */}
            <div className="flex items-center mb-6">
              <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-medium mr-6">
                {(user.displayName || user.email).charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {user.displayName || user.email.split('@')[0]}
                </h3>
                <p className="text-gray-600">{user.email}</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${
                  user.role === 'admin' ? 'bg-red-100 text-red-800' :
                  user.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                  user.role === 'viewer' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {roleDisplayNames[user.role] || user.role}
                </span>
              </div>
            </div>

            <hr className="mb-6" />

            {/* 기본 정보 폼 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  이메일
                </label>
                <input
                  type="email"
                  value={user.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">이메일은 변경할 수 없습니다.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  표시 이름
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="표시할 이름을 입력하세요"
                  />
                ) : (
                  <div className="flex items-center">
                    <span className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      {user.displayName || '설정되지 않음'}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  전화번호
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="010-1234-5678"
                  />
                ) : (
                  <div className="flex items-center">
                    <span className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                      {user.phoneNumber ? formatPhoneNumber(user.phoneNumber) : '설정되지 않음'}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  사용자 역할
                </label>
                <input
                  type="text"
                  value={roleDisplayNames[user.role] || user.role}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">역할은 관리자가 설정합니다.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  계정 생성일
                </label>
                <input
                  type="text"
                  value={formatDate(user.createdAt)}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            {/* 권한 정보 */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                보유 권한 ({user.permissions?.length || 0}개)
              </label>
              <div className="border border-gray-300 rounded-md p-4 bg-gray-50 max-h-40 overflow-y-auto">
                {user.permissions && user.permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.permissions.map((permission, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">보유한 권한이 없습니다.</p>
                )}
              </div>
            </div>

            {/* 관리자 전용 섹션 */}
            {(() => {
              const canManageUsers = hasPermission('manage_users');
              console.log('ProfilePage 권한 체크:', {
                user: !!user,
                userPermissions: user?.permissions,
                canManageUsers,
                hasManageUsersPermission: user?.permissions?.includes('manage_users')
              });
              return user && canManageUsers;
            })() && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-red-800 mb-1">관리자 도구</h3>
                    <p className="text-sm text-red-600">시스템 관리자 권한으로 사용할 수 있는 기능입니다.</p>
                  </div>
                  <button
                    onClick={handleUserManagement}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.25" />
                    </svg>
                    사용자 권한 관리
                  </button>
                </div>
              </div>
            )}

            {/* 액션 버튼 */}
            <div className="mt-6 flex justify-end space-x-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !displayName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {saving ? '저장 중...' : '저장'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  프로필 편집
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 관리자 전용: 사용자 활동 로그 */}
        {hasPermission('manage_users') && (
          <UserActivityLogs className="mb-6" />
        )}

        {/* 비밀번호 변경 섹션 */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">보안 설정</h2>
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none"
              >
                {showPasswordSection ? '숨기기' : '비밀번호 변경'}
              </button>
            </div>

            {showPasswordSection ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      현재 비밀번호
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="현재 비밀번호를 입력하세요"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      새 비밀번호
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="새 비밀번호 (최소 6자)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      새 비밀번호 확인
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="새 비밀번호를 다시 입력하세요"
                    />
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4">
                  <button
                    onClick={handleSendPasswordResetEmail}
                    disabled={sendingResetEmail}
                    className="text-sm text-gray-600 hover:text-gray-800 focus:outline-none disabled:opacity-50"
                  >
                    {sendingResetEmail ? '이메일 발송 중...' : '비밀번호 재설정 이메일 받기'}
                  </button>

                  <div className="space-x-3">
                    <button
                      onClick={() => {
                        setShowPasswordSection(false);
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                      }}
                      disabled={passwordChanging}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={handlePasswordChange}
                      disabled={passwordChanging || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      {passwordChanging ? '변경 중...' : '비밀번호 변경'}
                    </button>
                  </div>
                </div>

                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md">
                  <p className="font-medium mb-1">비밀번호 보안 가이드:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>최소 6자 이상 (8자 이상 권장)</li>
                    <li>영문 대소문자, 숫자, 특수문자 조합 권장</li>
                    <li>개인정보와 관련된 비밀번호는 피하세요</li>
                    <li>정기적으로 비밀번호를 변경하세요</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p>계정 보안을 위해 정기적으로 비밀번호를 변경하는 것을 권장합니다.</p>
                <p className="mt-2">마지막 비밀번호 변경: <span className="font-medium">알 수 없음</span></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;